/**
 * Utilitários de data/hora compartilhados (paridade com phonemanager-gui utils/dateUtils.js + FormatDate.vue).
 *
 * O backend serializa `LocalDateTime` sem fuso (ex.: "2024-05-10T13:45:00.123") e esses valores
 * estão em UTC. `parseBackendDate` interpreta strings sem designador como UTC; `formatDateTime`
 * renderiza no fuso do usuário (vindo de `/rest/user/timezoneByLogin`).
 */
import { create } from "zustand";

export function hasTimezoneDesignator(value: unknown): boolean {
  return typeof value === "string" && /(Z|[+-]\d{2}:?\d{2})$/.test(value.trim());
}

/** Converte valores do backend (string ISO sem zona = UTC, array Jackson, epoch, Date) em Date. */
export function parseBackendDate(input: unknown): Date | null {
  if (input === null || input === undefined || input === "") return null;

  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }

  if (typeof input === "number") {
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // Jackson sem JavaTimeModule configurado: [yyyy, MM, dd, HH, mm, ss, nanos]
  if (Array.isArray(input)) {
    const [y, mo = 1, d = 1, h = 0, mi = 0, s = 0, nanos = 0] = input.map((n) => Number(n));
    if (!y) return null;
    const date = new Date(Date.UTC(y, mo - 1, d, h, mi, s, Math.floor(nanos / 1_000_000)));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const rawValue = String(input).trim();
  if (!rawValue) return null;

  const normalizedRaw = rawValue.includes("T") || !rawValue.includes(" ") ? rawValue : rawValue.replace(" ", "T");

  const isoRegex =
    /^(\d{1,4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(\.\d+)?)?)?(Z|[+-]\d{2}:?\d{2})?$/;
  const match = isoRegex.exec(normalizedRaw);

  if (!match) {
    const fallback = new Date(rawValue);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  let year = parseInt(match[1], 10);
  if (match[1].length < 4) year = 2000 + year;
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  const hour = match[4] ? parseInt(match[4], 10) : 0;
  const minute = match[5] ? parseInt(match[5], 10) : 0;
  const second = match[6] ? parseInt(match[6], 10) : 0;
  const millisecond = match[7] ? parseInt(match[7].slice(1).padEnd(3, "0").slice(0, 3), 10) : 0;
  const zone = match[8] || null;

  if (zone) {
    const zDate = new Date(normalizedRaw);
    if (!Number.isNaN(zDate.getTime())) return zDate;
  }

  const utc = new Date(Date.UTC(year, month, day, hour, minute, second, millisecond));
  return Number.isNaN(utc.getTime()) ? null : utc;
}

function isValidTimeZone(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export type DateTimeStyle = "dateTime" | "date" | "time";

/**
 * Formata no fuso informado como "dd/MM/yyyy HH:mm:ss" (pt-BR).
 * Sem fuso válido, usa o fuso do navegador.
 */
export function formatDateTime(value: unknown, tz?: string | null, style: DateTimeStyle = "dateTime"): string {
  const date = parseBackendDate(value);
  if (!date) return value == null ? "" : String(value);
  const timeZone = isValidTimeZone(tz) ? tz : undefined;
  const opts: Intl.DateTimeFormatOptions = { timeZone, hourCycle: "h23" };
  if (style !== "time") Object.assign(opts, { day: "2-digit", month: "2-digit", year: "numeric" });
  if (style !== "date") Object.assign(opts, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return new Intl.DateTimeFormat("pt-BR", opts).format(date).replace(",", "");
}

/** Converte uma data "yyyy-MM-dd" (seletor) no formato aceito pelo backend: yyyy-MM-dd'T'HH:mm:ss.SSS'Z'. */
export function toBackendDateParam(day: string | null | undefined, endOfDay = false): string | undefined {
  if (!day) return undefined;
  const d = day.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return undefined;
  return `${d}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}Z`;
}

/** "yyyy-MM-dd" do primeiro dia do mês corrente (default do filtro do GUI). */
export function firstDayOfCurrentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

/** "yyyy-MM-dd" de hoje (data local). */
export function todayIsoDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** "yyyy-MM-dd" (UTC) de um valor do backend — usado como limite mínimo dos seletores. */
export function backendDateToIsoDay(value: unknown): string | undefined {
  const d = parseBackendDate(value);
  return d ? d.toISOString().slice(0, 10) : undefined;
}

/** Duração em ms → "HH:MM:SS" ("00:00:00" para vazio ou ≤ 0), como o GUI. */
export function formatDurationMS(durationMS?: number | string | null): string {
  const ms = typeof durationMS === "string" ? Number(durationMS) : durationMS;
  if (ms === undefined || ms === null || Number.isNaN(ms) || ms <= 0) return "00:00:00";
  let dt = Math.floor(ms / 1000);
  const seconds = dt % 60;
  dt = Math.floor(dt / 60);
  const minutes = dt % 60;
  const hours = Math.floor(dt / 60);
  return [hours, minutes, seconds].map((n) => String(n).padStart(2, "0")).join(":");
}

/* ——— Fuso do usuário logado ——— */

type UserTimezoneState = {
  /** Nome IANA (ex.: "America/Sao_Paulo"); null = fuso do navegador. */
  timezone: string | null;
  setTimezone: (tz: string | null) => void;
};

export const useUserTimezoneStore = create<UserTimezoneState>((set) => ({
  timezone: null,
  setTimezone: (timezone) => set({ timezone }),
}));

/** Extrai o nome IANA da resposta de `/rest/user/timezoneByLogin` (TZChildren) ou de uma string. */
export function extractTimezoneName(data: unknown): string | null {
  if (!data) return null;
  if (typeof data === "string") return data.trim() || null;
  if (typeof data === "object") {
    const o = data as { name?: string; id?: string };
    return o.name ?? o.id ?? null;
  }
  return null;
}

/** Hook: formata datas no fuso do usuário logado. */
export function useFormatDateTime() {
  const tz = useUserTimezoneStore((s) => s.timezone);
  return (value: unknown, style: DateTimeStyle = "dateTime") => formatDateTime(value, tz, style);
}
